import type { PropsWithChildren } from 'react'
import { useEffect, useMemo, useState } from 'react'
import { useAppSelector } from 'hooks/reduxHooks'
import { createIntl, createIntlCache, RawIntlProvider } from 'react-intl'
import {
  DEFAULT_LOCALE,
  getLocaleDirection,
  getLocaleLanguageCode,
  getLocaleWithWesternDigits,
} from './util/locale'
import { usePseudoloc, type PseudolocMode } from './PseudolocContext'
import { setCurrentIntl } from './util/currentIntl'
export type LocaleMessages = Record<string, string>
const emptyMessages: LocaleMessages = {}

type MessageTransformer = (messages: LocaleMessages) => LocaleMessages
type MessageEntry = {
  defaultMessage: string
  description?: string
}

interface I18nProviderProps {
  messages?: LocaleMessages
  messagesLocale?: string
}

function mapLocaleMessages(
  messages: Record<string, MessageEntry>,
): LocaleMessages {
  return Object.fromEntries(
    Object.entries(messages).map(([key, value]) => [
      key,
      typeof value === 'string' ? value : value.defaultMessage,
    ]),
  )
}

export function shouldSkipRuntimeLocaleMessages(
  locale: string,
  pseudolocMode: PseudolocMode,
  buildLocale: string | undefined = process.env.BUILD_LOCALE,
) {
  return (
    pseudolocMode === 'off' &&
    (locale === DEFAULT_LOCALE || buildLocale === locale)
  )
}

export function loadLocaleMessages(
  locale: string,
  pseudolocMode: PseudolocMode,
): Promise<LocaleMessages> {
  if (shouldSkipRuntimeLocaleMessages(locale, pseudolocMode)) {
    return Promise.resolve(emptyMessages)
  }
  return import(
    `./lang/${locale}.json`
  )
    .then((mod) =>
      mapLocaleMessages(mod.default as Record<string, MessageEntry>),
    )
    .catch(() => emptyMessages)
}

export function I18nProvider({
  messages,
  messagesLocale,
  children,
}: PropsWithChildren<I18nProviderProps>) {
  const geoLocale = useAppSelector((state) => state.geo.locale)
  const locale = typeof geoLocale === 'string' ? geoLocale : DEFAULT_LOCALE
  const { mode } = usePseudoloc()
  const [transformers, setTransformers] = useState<
    Partial<Record<PseudolocMode, MessageTransformer>>
  >({})
  const [localeMessages, setLocaleMessages] = useState<LocaleMessages>(
    messages ?? emptyMessages,
  )

  useEffect(() => {
    if (messages && locale === messagesLocale) {
      setLocaleMessages(messages)
      return
    }

    let isMounted = true

    loadLocaleMessages(locale, mode).then((loadedMessages) => {
      if (isMounted) {
        setLocaleMessages(loadedMessages)
      }
    })

    return () => {
      isMounted = false
    }
  }, [locale, messages, messagesLocale, mode])

  useEffect(() => {
    document.documentElement.lang = getLocaleLanguageCode(locale)
    document.documentElement.dir = getLocaleDirection(locale)
  }, [locale])

  useEffect(() => {
    if (mode === 'off') return

    // Note: Both transformers are loaded regardless of the active mode since
    // they live in the same module. This avoids re-importing on mode switch.
    import('./util/pseudoloc').then((module) => {
      setTransformers((prev) => ({
        ...prev,
        full: module.pseudolocalizeMessages,
        length: module.lengthPseudolocalizeMessages,
      }))
    })
  }, [mode])

  const processedMessages = useMemo(() => {
    if (mode !== 'off') {
      const transformer = transformers[mode]
      if (transformer) {
        return transformer(localeMessages)
      }
    }
    return localeMessages
  }, [localeMessages, mode, transformers])

  const intl = useMemo(() => {
    return createIntl(
      {
        locale: getLocaleWithWesternDigits(locale),
        messages: processedMessages,
        defaultLocale: DEFAULT_LOCALE,
        onError(err) {
          if (err.code === 'MISSING_TRANSLATION') {
            return
          }
          console.error(err)
        },
      },
      createIntlCache(),
    )
  }, [locale, processedMessages])

  setCurrentIntl(intl)

  return <RawIntlProvider value={intl}>{children}</RawIntlProvider>
}
